import { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleCheck,
  faCircleInfo,
  faClock,
  faFileLines,
  faUser,
  faUserGroup,
} from "@fortawesome/pro-regular-svg-icons";
import { IconDefinition } from "@fortawesome/fontawesome-svg-core";
import cx from "classnames";
import { useTranslation } from "react-i18next";
import { useMedia } from "react-use";
import { AnalyticsWidgetUiDetailsDto } from "../../../types/analytics";
import { UsageStatusDataset } from "../../../types/analytics/UsageStatusDataset";
import { UsageDistributionSegment } from "../../../types/analytics/UsageDistributionSegment";
import {
  buildDistributionSegments,
  COMPLETED_KEY,
  foldPartiallyCompleteIntoWaitingForEmployee,
  getCompletionRate,
  getSegmentPercentages,
  getUsageStatusHeadlineCounts,
  NOT_STARTED_KEY,
  WAITING_FOR_EMPLOYEE_KEY,
  WAITING_FOR_MANAGER_KEY,
} from "../../../helpers/usage/usageStatusChartHelpers";
import { getCompletionBand } from "../../../helpers/usage/usageCompletionBands";
import { shouldShowWaitingForManagerPopover } from "../../../helpers/usage/usageTableFilterHelpers";
import colourHelper from "../../../helpers/colourHelper";
import { useCountUpProgress } from "../../../hooks";
import { ModalPopup } from "../../common";
import UsageStatusDistributionBar, {
  UsageStatusSegmentClick,
} from "./UsageStatusDistributionBar";

interface UsageStatusOverviewProps {
  widget: AnalyticsWidgetUiDetailsDto;
  variant?: "default" | "compact" | "hero";
  waitingOnManagerStalledCount?: number;
  agingCardsUnlocked?: boolean;
  onSegmentFilter?: (segment: UsageStatusSegmentClick) => void;
  onGoToManagers?: () => void;
  selectedStatusKey?: string | null;
}

function UsageStatusOverview({
  widget,
  variant = "default",
  waitingOnManagerStalledCount = 0,
  agingCardsUnlocked = false,
  onSegmentFilter,
  onGoToManagers,
  selectedStatusKey = null,
}: UsageStatusOverviewProps) {
  const { t } = useTranslation();
  const datasets = (widget.datasets ?? []) as UsageStatusDataset[];

  if (datasets.length === 0) {
    return null;
  }

  if (datasets.length > 1) {
    const closedJourneys = datasets.filter((dataset) => dataset.isClosed);

    if (closedJourneys.length === 0) {
      return null;
    }

    const closedJourneysLabel = t(
      "Analytics.Widgets.Usage.UsageSummary.Status.ClosedJourneys",
      "Closed journeys:",
    );

    return (
      <div className="mb-5 flex flex-wrap items-center gap-2">
        <span
          id="usage-closed-journeys-label"
          className="text-sm font-medium text-gray-600"
        >
          {closedJourneysLabel}
        </span>
        <ul
          className="flex flex-wrap gap-2"
          aria-labelledby="usage-closed-journeys-label"
        >
          {closedJourneys.map((journey) => {
            const title = journey.translateTitle
              ? t(journey.title, journey.title)
              : journey.title;

            return (
              <li
                key={journey.title}
                className="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-gray-50 px-2.5 py-1 text-sm text-gray-700"
              >
                <span>{title}</span>
                <span className="rounded-sm bg-gray-200 px-1.5 py-0.5 text-xs font-semibold text-gray-700">
                  {t(
                    "Analytics.Widgets.Usage.UsageSummary.Status.Closed",
                    "Closed",
                  )}
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  const dataset = datasets[0];
  const {
    assignedCount,
    completedCount,
    dropoutCount,
    inProgressCount,
    notStartedCount,
  } = getUsageStatusHeadlineCounts(dataset);

  const headlineItems = [
    {
      label: t(
        "Analytics.Widgets.Usage.UsageSummary.Status.Assigned",
        "Assigned",
      ),
      value: assignedCount,
    },
    {
      label: t(
        "Analytics.Widgets.Usage.UsageSummary.Status.Completed",
        "Completed",
      ),
      value: completedCount,
    },
    {
      label: t(
        "Analytics.Widgets.Usage.UsageSummary.Status.NotStarted",
        "Not started",
      ),
      value: notStartedCount,
    },
    {
      label: t(
        "Analytics.Widgets.Usage.UsageSummary.Status.InProgress",
        "In progress",
      ),
      value: inProgressCount,
    },
  ];

  if (variant === "hero") {
    return (
      <UsageStatusHeroOverview
        widget={widget}
        journeyTitle={
          dataset.translateTitle
            ? t(dataset.title, dataset.title)
            : dataset.title
        }
        completedCount={completedCount}
        assignedCount={assignedCount}
        waitingOnManagerStalledCount={waitingOnManagerStalledCount}
        agingCardsUnlocked={agingCardsUnlocked}
        dropoutCount={dropoutCount}
        onSegmentFilter={onSegmentFilter}
        onGoToManagers={onGoToManagers}
        selectedStatusKey={selectedStatusKey}
      />
    );
  }

  if (variant === "compact") {
    return (
      <div>
        <dl className="flex flex-wrap divide-x divide-gray-200">
          {headlineItems.map((item) => (
            <div key={item.label} className="px-4 py-1 first:pl-0">
              <dt className="text-xs text-gray-600">{item.label}</dt>
              <dd className="text-lg font-semibold text-gray-800">
                {item.value.toLocaleString()}
              </dd>
            </div>
          ))}
        </dl>
        <UsageDropoutNote
          dropoutCount={dropoutCount}
          className="mt-2 text-xs text-gray-600"
        />
      </div>
    );
  }

  return (
    <div className="mb-5">
      <dl className="grid grid-cols-2 border-y border-gray-200 py-3 lg:grid-cols-4">
        {headlineItems.map((item, index) => (
          <div
            key={item.label}
            className={`px-4 py-2 ${
              index % 2 === 1 ? "border-l border-gray-200" : ""
            } ${index >= 2 ? "border-t border-gray-200 lg:border-t-0" : ""} ${
              index > 0 ? "lg:border-l lg:border-gray-200" : ""
            }`}
          >
            <dt className="text-sm text-gray-600">{item.label}</dt>
            <dd className="mt-0.5 text-2xl font-semibold text-gray-800">
              {item.value.toLocaleString()}
            </dd>
          </div>
        ))}
      </dl>
      <UsageDropoutNote
        dropoutCount={dropoutCount}
        className="mt-3 text-sm text-gray-600"
      />
    </div>
  );
}

/**
 * Historical dropouts sit outside the Assigned total, so every variant has to
 * explain the discrepancy rather than silently drop the difference.
 */
function UsageDropoutNote({
  dropoutCount,
  className,
}: {
  dropoutCount: number;
  className: string;
}) {
  const { t } = useTranslation();

  if (dropoutCount === 0) {
    return null;
  }

  return (
    <p className={className}>
      {dropoutCount === 1
        ? t(
            "Analytics.Widgets.Usage.UsageSummary.Status.DropoutNoteSingle",
            "1 historical dropout is shown for reconciliation and is not included in Assigned.",
          )
        : t(
            "Analytics.Widgets.Usage.UsageSummary.Status.DropoutNoteMultiple",
            "{{count}} historical dropouts are shown for reconciliation and are not included in Assigned.",
            { count: dropoutCount },
          )}
    </p>
  );
}

function UsageStatusHeroOverview({
  widget,
  journeyTitle,
  completedCount,
  assignedCount,
  waitingOnManagerStalledCount,
  agingCardsUnlocked,
  dropoutCount,
  onSegmentFilter,
  onGoToManagers,
  selectedStatusKey,
}: {
  widget: AnalyticsWidgetUiDetailsDto;
  journeyTitle: string;
  completedCount: number;
  assignedCount: number;
  waitingOnManagerStalledCount: number;
  agingCardsUnlocked: boolean;
  dropoutCount: number;
  onSegmentFilter?: (segment: UsageStatusSegmentClick) => void;
  onGoToManagers?: () => void;
  selectedStatusKey?: string | null;
}) {
  const { t } = useTranslation();
  const prefersReducedMotion = useMedia(
    "(prefers-reduced-motion: reduce)",
    false,
  );
  const [managerForkOpen, setManagerForkOpen] = useState(false);

  const completionPercent = getCompletionRate(completedCount, assignedCount);
  const hasAssignments = assignedCount > 0;
  const allAssignedComplete =
    hasAssignments && completedCount === assignedCount;
  const band = getCompletionBand(completionPercent);

  const countUpProgress = useCountUpProgress(
    `${completionPercent}:${completedCount}`,
    !prefersReducedMotion,
  );
  const animatedPercent = Math.round(completionPercent * countUpProgress);
  const animatedCompletedCount = Math.round(completedCount * countUpProgress);

  const segments = foldPartiallyCompleteIntoWaitingForEmployee(
    buildDistributionSegments(widget),
    t(
      "Analytics.Widgets.Usage.UsageSummary.Legend.WaitingForEmployee",
      "Waiting for Employee",
    ),
  );
  const percentages = getSegmentPercentages(segments);
  const showStalledCallout =
    agingCardsUnlocked && waitingOnManagerStalledCount > 0;

  const waitingForManagerSegment = segments.find(
    (segment) => segment.id === WAITING_FOR_MANAGER_KEY,
  );

  const handleSegmentClick = (segment: UsageStatusSegmentClick) => {
    if (shouldShowWaitingForManagerPopover(segment.id) && onGoToManagers) {
      setManagerForkOpen(true);
      return;
    }

    onSegmentFilter?.(segment);
  };

  const viewPeople = () => {
    setManagerForkOpen(false);

    if (!waitingForManagerSegment) {
      return;
    }

    onSegmentFilter?.({
      id: waitingForManagerSegment.id,
      displayLabel: waitingForManagerSegment.displayLabel,
      value: waitingForManagerSegment.value,
    });
  };

  const seeManagers = () => {
    setManagerForkOpen(false);
    onGoToManagers?.();
  };

  return (
    <div>
      <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0">
          {journeyTitle && (
            <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
              {journeyTitle}
            </p>
          )}
          <div className="mt-1 flex flex-wrap items-baseline gap-x-3">
            {hasAssignments ? (
              <>
                <span
                  className="flex items-baseline font-bold leading-none tabular-nums"
                  style={{ color: band.textColour }}
                >
                  <span className="text-5xl md:text-6xl">
                    {animatedPercent}
                  </span>
                  <span className="text-2xl md:text-3xl">%</span>
                </span>
                <span
                  className="text-lg font-medium"
                  style={{ color: band.textColour }}
                >
                  {t(
                    "Analytics.Widgets.Usage.UsageSummary.Status.Completed",
                    "Completed",
                  )}
                </span>
              </>
            ) : (
              <span
                aria-hidden
                className="text-5xl font-bold leading-none text-gray-500 md:text-6xl"
              >
                -
              </span>
            )}
          </div>
          <p className="mt-2 text-sm text-gray-600">
            {hasAssignments ? (
              <>
                <span className="font-medium tabular-nums text-gray-700">
                  {animatedCompletedCount.toLocaleString()}
                </span>{" "}
                {t("Analytics.Widgets.Usage.Band.AssignedOf", "of")}{" "}
                {assignedCount.toLocaleString()}{" "}
                {t("Analytics.Widgets.Usage.Band.AssignedSuffix", "assigned")}
              </>
            ) : (
              t(
                "Analytics.Widgets.Usage.UsageSummary.Status.NoneAssigned",
                "No one is assigned in this view.",
              )
            )}
          </p>
          {allAssignedComplete && (
            <p className="mt-1 text-sm text-gray-600">
              {t(
                "Analytics.Widgets.Usage.UsageSummary.Status.AllAssignedComplete",
                "Everyone assigned has finished.",
              )}
            </p>
          )}
        </div>

        {showStalledCallout && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 md:max-w-xs">
            <div className="flex items-center gap-2.5">
              <span
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100"
                aria-hidden
              >
                <FontAwesomeIcon icon={faClock} className="text-amber-700" />
              </span>
              <span className="text-sm font-semibold text-amber-700">
                {t(
                  "Analytics.Widgets.Usage.Band.Waiting14Days",
                  "Waiting 14+ days",
                )}
              </span>
            </div>
            <p className="mt-1 text-2xl font-bold tabular-nums text-amber-700">
              {waitingOnManagerStalledCount.toLocaleString()}
            </p>
            <p className="mt-1 text-xs text-gray-600">
              {t(
                "Analytics.Widgets.Usage.Band.Waiting14DaysDescription",
                "Journeys awaiting action for more than 14 days",
              )}
            </p>
          </div>
        )}
      </div>

      <div className="mt-5">
        <UsageStatusDistributionBar
          segments={segments}
          onSegmentFilter={handleSegmentClick}
        />
      </div>

      {segments.length > 0 && (
        <>
          <ul className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {segments.map((segment) => (
              <li key={`card-${segment.id}`}>
                <UsageStatusCard
                  segment={segment}
                  percentage={percentages[segment.id] ?? 0}
                  isSelected={selectedStatusKey === segment.id}
                  onClick={
                    onSegmentFilter || onGoToManagers
                      ? () =>
                          handleSegmentClick({
                            id: segment.id,
                            displayLabel: segment.displayLabel,
                            value: segment.value,
                          })
                      : undefined
                  }
                />
              </li>
            ))}
          </ul>

          <p className="mt-4 flex items-center justify-center gap-1.5 text-xs text-gray-500">
            <FontAwesomeIcon icon={faCircleInfo} aria-hidden />
            {t(
              "Analytics.Widgets.Usage.Band.RoundingNote",
              "Percentages may not add up to 100% due to rounding.",
            )}
          </p>
        </>
      )}

      <UsageDropoutNote
        dropoutCount={dropoutCount}
        className="mt-2 text-xs text-gray-600"
      />

      <ModalPopup
        isOpen={managerForkOpen}
        onOpenChange={setManagerForkOpen}
        width="TINY"
        title={t(
          "Analytics.Widgets.Usage.Band.ManagerFork.Title",
          "Waiting for manager",
        )}
        footerComponent={
          <div className="flex flex-col gap-2 sm:items-stretch">
            <button
              type="button"
              className="btn-primary w-full justify-center text-center"
              onClick={seeManagers}
            >
              {t(
                "Analytics.Widgets.Usage.Band.ManagerFork.SeeManagers",
                "See which managers are holding things up",
              )}
            </button>
            <button
              type="button"
              className="btn-secondary w-full justify-center text-center"
              onClick={viewPeople}
            >
              {t(
                "Analytics.Widgets.Usage.Band.ManagerFork.ViewPeople",
                "View the {{count}} people waiting",
                { count: waitingForManagerSegment?.value ?? 0 },
              )}
            </button>
          </div>
        }
      >
        <p className="text-sm text-gray-600">
          {t(
            "Analytics.Widgets.Usage.Band.ManagerFork.Description",
            "These journeys are waiting on managers. You can see the list of whose journeys are waiting on managers, or see which managers are holding things up.",
          )}
        </p>
      </ModalPopup>
    </div>
  );
}

const statusCardIcons: Record<string, IconDefinition> = {
  [NOT_STARTED_KEY]: faFileLines,
  [WAITING_FOR_EMPLOYEE_KEY]: faUser,
  [WAITING_FOR_MANAGER_KEY]: faUserGroup,
  [COMPLETED_KEY]: faCircleCheck,
};

function UsageStatusCard({
  segment,
  percentage,
  isSelected = false,
  onClick,
}: {
  segment: UsageDistributionSegment;
  percentage: number;
  isSelected?: boolean;
  onClick?: () => void;
}) {
  const { t } = useTranslation();

  const statusCardDescriptions: Record<string, string> = {
    [NOT_STARTED_KEY]: t(
      "Analytics.Widgets.Usage.Band.StatusCard.NotStarted",
      "Journeys not yet started",
    ),
    [WAITING_FOR_EMPLOYEE_KEY]: t(
      "Analytics.Widgets.Usage.Band.StatusCard.WaitingForEmployee",
      "Employees completing their step",
    ),
    [WAITING_FOR_MANAGER_KEY]: t(
      "Analytics.Widgets.Usage.Band.StatusCard.WaitingForManager",
      "With the manager for their step",
    ),
    [COMPLETED_KEY]: t(
      "Analytics.Widgets.Usage.Band.StatusCard.Completed",
      "Journeys completed",
    ),
  };

  const icon = statusCardIcons[segment.id];
  const description = statusCardDescriptions[segment.id];
  const rgb = colourHelper.convertHexToRgbNumbersString(segment.color);
  const figureColour = colourHelper.shadeColour(segment.color, -18);

  const content = (
    <>
      <div className="flex items-center gap-2.5">
        {icon && (
          <span
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full"
            style={{ backgroundColor: `rgba(${rgb}, 0.18)` }}
            aria-hidden
          >
            <FontAwesomeIcon icon={icon} style={{ color: figureColour }} />
          </span>
        )}
        <span className="text-sm font-semibold text-gray-800">
          {segment.displayLabel}
        </span>
      </div>

      <p
        className="mt-3 text-2xl font-bold tabular-nums"
        style={{ color: figureColour }}
      >
        {segment.value.toLocaleString()}
      </p>

      <p className="mt-1">
        <span
          className="inline-block rounded-full px-2 py-0.5 text-xs font-semibold"
          style={{
            backgroundColor: `rgba(${rgb}, 0.18)`,
            color: figureColour,
          }}
        >
          {percentage}%
        </span>
      </p>

      {description && (
        <p className="mt-3 border-t border-gray-100 pt-2.5 text-xs text-gray-500">
          {description}
        </p>
      )}
    </>
  );

  const cardClasses = cx(
    "h-full w-full rounded-lg border p-4 text-left",
    "transition duration-150 ease-out motion-reduce:transition-none",
    isSelected ? "shadow-sm" : "border-gray-200 bg-white",
    onClick && [
      "hover:-translate-y-0.5 hover:shadow-md",
      "focus:outline-none",
      "focus-visible:-translate-y-0.5 focus-visible:shadow-md",
      "focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2",
      "motion-reduce:hover:translate-y-0 motion-reduce:focus-visible:translate-y-0",
      !isSelected && "hover:border-gray-300",
    ],
  );

  const selectedStyle = isSelected
    ? {
        borderColor: figureColour,
        backgroundColor: `rgba(${rgb}, 0.08)`,
      }
    : undefined;

  if (!onClick) {
    return (
      <div className={cardClasses} style={selectedStyle}>
        {content}
      </div>
    );
  }

  return (
    <button
      type="button"
      className={cardClasses}
      style={selectedStyle}
      aria-pressed={isSelected}
      onClick={onClick}
    >
      {content}
    </button>
  );
}

export default UsageStatusOverview;
